import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Card, Table, Tooltip, Button, Space, Input, Select, Row, Col, Typography, Badge,
  message, notification, Popover, Modal, Spin, InputNumber, Pagination, Tag, Drawer,
  Descriptions, Slider,
} from 'antd';
import {
  ExclamationCircleOutlined, SearchOutlined, MailOutlined, GlobalOutlined, LinkOutlined,
  InboxOutlined, ShoppingCartOutlined, DeleteOutlined, SettingOutlined, MenuOutlined,
  PlusOutlined, CloseOutlined, EyeOutlined, EyeInvisibleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import relativeTime from 'dayjs/plugin/relativeTime';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useColumnSettings } from '../hooks/useColumnSettings';

import { oilOrdersApi, OilSupplier, OilProduct } from '../api/oilOrders';
import { getCatalogItem, CatalogItem } from '../api/productCatalog';
import { getProductTypeCatalog, ProductType } from '../api/productTypeCatalog';
import api from '../api/client';
import CounterpartyInfoDrawer from '../components/CounterpartyInfoDrawer';
import OrderSubTable from '../components/OrderSubTable';
import BrandLogo from '../components/BrandLogo';
import { useNavigate } from 'react-router-dom';
import useOilCartStore from '../store/oilCartStore';
import useTaskStore from '../store/taskStore';

dayjs.extend(relativeTime);
dayjs.locale('ru');

const { Title, Text } = Typography;

// ─── Draggable Header Cell ──────────────────────────────────────────────────

function DragHeaderCell({ columnKey, children, className, style, ...rest }: any) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: columnKey || '__noop__',
    disabled: !columnKey,
  });
  if (!columnKey) return <th className={className} style={style} {...rest}>{children}</th>;
  return (
    <th
      ref={setNodeRef}
      className={className}
      style={{
        ...style,
        transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        whiteSpace: 'nowrap',
      }}
      {...attributes}
      {...listeners}
      {...rest}
    >
      {children}
    </th>
  );
}

// ─── Supplier Card ───────────────────────────────────────────────────────────

const BADGE_COLOR: Record<string, string> = {
  fresh:     'success',
  requested: 'warning',
  stale:     'error',
};

// Цвет верхней полосы карточки поставщика
const STATUS_TOP_COLOR: Record<string, string> = {
  fresh:     '#52c41a',  // зелёный — свежий прайс
  requested: '#faad14',  // жёлтый — ждём ответа
  stale:     '#ff4d4f',  // красный — устарел
};

interface CounterpartyOption { id: number; label: string; email: string | null; }

function SupplierCard({
  supplier,
  loading,
  activeImport,
  onRequest,
  onLink,
  linking,
  onDrop,
  onCardClick,
  onClearPrices,
  webUrl,
  onFetchWeb,
}: {
  supplier: OilSupplier;
  loading: boolean;
  activeImport?: boolean;
  onRequest: (id: number) => void;
  onLink: (supplierId: number, email: string) => Promise<void>;
  linking: boolean;
  onDrop: (supplierId: number, file: File) => void;
  onCardClick?: (counterpartyId: number) => void;
  onClearPrices: (supplierId: number) => void;
  webUrl?: string;
  onFetchWeb?: (supplierId: number) => void;
}) {
  const [popOpen, setPopOpen] = useState(false);
  const [editEmail, setEditEmail] = useState(supplier.email ?? '');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isStale = supplier.status.badge === 'stale';

  const nameEl = isStale ? (
    <Tooltip title={supplier.status.label || 'Прайс устарел'}>
      <span style={{ color: '#ff4d4f' }}>
        <ExclamationCircleOutlined style={{ marginRight: 4 }} />
        {supplier.name}
      </span>
    </Tooltip>
  ) : <span>{supplier.name}</span>;

  const dateStr = supplier.last_price_at ? dayjs(supplier.last_price_at).format('DD.MM.YY') : 'нет данных';
  const relStr  = supplier.last_price_at && supplier.days_since_update !== null
    ? `(${supplier.days_since_update} дн. назад)` : '';

  // Итоговый email для отображения: собственный > контрагента
  const effectiveEmail = supplier.email || supplier.counterparty_email;

  const linkContent = (
    <div style={{ width: 260 }}>
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Email (для входящих писем с прайсом)</Text>
        <Input
          size="small"
          style={{ marginTop: 4 }}
          value={editEmail}
          onChange={e => setEditEmail(e.target.value)}
          placeholder="email@supplier.ru"
          allowClear
        />
      </div>
      <Space>
        <Button
          size="small"
          type="primary"
          loading={linking}
          onClick={async (e) => {
            e.stopPropagation();
            await onLink(supplier.id, editEmail);
            setPopOpen(false);
          }}
        >
          Сохранить
        </Button>
        <Button size="small" onClick={(e) => { e.stopPropagation(); setPopOpen(false); }}>Отмена</Button>
      </Space>
    </div>
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onDrop(supplier.id, file);
  };

  const topColor = STATUS_TOP_COLOR[supplier.status.badge] ?? '#ff4d4f';
  const isClickable = !!supplier.counterparty_id && !!onCardClick;
  const showUpdatingBadge = !!activeImport;

  return (
    <Card
      size="small"
      hoverable={isClickable}
      onClick={() => { if (isClickable) onCardClick!(supplier.counterparty_id!); }}
      style={dragOver ? {
        minWidth: 230,
        border: '2px dashed #1890ff',
        background: '#e6f7ff',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'border 0.15s, background 0.15s',
      } : {
        minWidth: 230,
        border: '1px solid #f0f0f0',
        borderTop: `3px solid ${topColor}`,
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'border 0.15s, background 0.15s',
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Title level={5} style={{ marginBottom: 4 }}>{nameEl}</Title>
      <div style={{ marginBottom: 4 }}>
        <Text type="secondary">Обновлён: {dateStr} </Text>
        {relStr && <Text type="secondary" style={{ fontSize: 12 }}>{relStr}</Text>}
      </div>

      {/* Контрагент / Email */}
      <div style={{ marginBottom: 8 }}>
        {effectiveEmail ? (
          <Text style={{ fontSize: 12 }}>
            <MailOutlined style={{ marginRight: 4, color: '#52c41a' }} />
            {effectiveEmail}
          </Text>
        ) : supplier.source_type === 'email' ? (
          <Text type="secondary" style={{ fontSize: 12 }}>Email не задан</Text>
        ) : null}{' '}
        {/* Email-редактирование только для email-поставщиков */}
        {supplier.source_type === 'email' && (
          <Popover
            open={popOpen}
            onOpenChange={open => { if (open) setEditEmail(supplier.email ?? ''); setPopOpen(open); }}
            content={linkContent}
            title="Настройка поставщика"
            trigger="click"
          >
            <a style={{ fontSize: 12 }} onClick={e => e.stopPropagation()}>изменить</a>
          </Popover>
        )}
      </div>

      {supplier.source_type === 'web' && (
        <div style={{ marginBottom: 4 }}>
          {webUrl ? (
            <a style={{ fontSize: 12, color: '#8c8c8c' }} href={webUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              <GlobalOutlined style={{ marginRight: 4 }} />Обновление через сайт
            </a>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}><GlobalOutlined style={{ marginRight: 4 }} />Обновление через сайт</Text>
          )}
        </div>
      )}
      {showUpdatingBadge ? (
        <div style={{ marginBottom: 8 }}>
          <Badge status="processing" text="Обновляю прайс..." />
        </div>
      ) : (
        supplier.status.badge !== 'fresh' && supplier.status.label ? (
          <div style={{ marginBottom: 8 }}>
            <Badge status={BADGE_COLOR[supplier.status.badge] as any} text={supplier.status.label} />
          </div>
        ) : null
      )}

      <Space size={4} wrap>
        <Button
          size="small"
          loading={loading}
          disabled={loading}
          icon={supplier.source_type === 'web' ? <GlobalOutlined /> : <MailOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            if (supplier.source_type === 'web' && onFetchWeb) {
              onFetchWeb(supplier.id);
            } else {
              onRequest(supplier.id);
            }
          }}
        >
          {supplier.source_type === 'web'
            ? (loading ? 'Загружаю...' : 'Обновить с сайта')
            : (loading ? 'Отправляю запрос' : 'Запросить прайс')}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          style={{ display: 'none' }}
          onClick={e => e.stopPropagation()}
          onChange={e => { const f = e.target.files?.[0]; if (f) onDrop(supplier.id, f); e.target.value = ''; }}
        />
      </Space>
      {/* Ссылка для загрузки прайса + очистка */}
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <a
          style={{ fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
        >
          <InboxOutlined style={{ marginRight: 4 }} />
          перетащите прайс сюда
        </a>
        {supplier.last_price_at && (
          <Tooltip title="Очистить прайс">
            <DeleteOutlined
              style={{ fontSize: 13, color: '#ff4d4f', cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); onClearPrices(supplier.id); }}
            />
          </Tooltip>
        )}
      </div>

      {dragOver && (
        <div style={{ textAlign: 'center', marginTop: 8, color: '#1890ff', fontSize: 12 }}>
          <InboxOutlined style={{ fontSize: 20 }} /><br />Отпустите для загрузки
        </div>
      )}
    </Card>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function OilOrdersPage() {
  const [suppliers, setSuppliers] = useState<OilSupplier[]>([]);
  const [products, setProducts] = useState<OilProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string | undefined>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [requesting, setRequesting] = useState<Set<number>>(new Set());
  const [checkingEmails, setCheckingEmails] = useState(false);
  const [counterparties, setCounterparties] = useState<CounterpartyOption[]>([]);
  const [linking, setLinking] = useState(false);
  const [filterBrand, setFilterBrand] = useState<string | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Отслеживание "виртуального" ховера в объединённых ячейках
  const [hoverRowKey, setHoverRowKey] = useState<string | null>(null);
  // Drawer контрагента
  const [cpDrawer, setCpDrawer] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  // Drag-and-drop upload modal
  const [uploadModal, setUploadModal] = useState<{ supplierId: number; supplierName: string; file: File } | null>(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const { loadCart, setQty, getQty, hasItems, hasItemsForSize, items: cartItems } = useOilCartStore();
  const cartBarRef = useRef<HTMLDivElement>(null);
  const [cartBarHeight, setCartBarHeight] = useState(0);
  const filterBarRef = useRef<HTMLDivElement>(null);
  const [filterBarHeight, setFilterBarHeight] = useState(0);
  // Отступы таблицы (localStorage)
  const [tablePadding, setTablePadding] = useState(() => {
    try { const v = localStorage.getItem('oil-table-padding'); return v ? JSON.parse(v) : { left: 0, right: 0 }; }
    catch { return { left: 0, right: 0 }; }
  });
  // Column DnD
  const supplierIds = React.useMemo(() => suppliers.map(s => s.id), [suppliers]);
  const { columnOrder, reorderStatic, reorderSupplier } = useColumnSettings(supplierIds);
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = String(active.id), to = String(over.id);
    if (from.startsWith('supplier:') && to.startsWith('supplier:')) reorderSupplier(from, to);
    else if (!from.startsWith('supplier:') && !to.startsWith('supplier:')) reorderStatic(from, to);
  }, [reorderStatic, reorderSupplier]);
  // Настройки
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [typeOrder, setTypeOrder] = useState<string[]>([]);
  const [brandOrder, setBrandOrder] = useState<string[]>([]);
  const [modelOrder, setModelOrder] = useState<string[]>([]);
  const [nevaConfig, setNevaConfig] = useState<{ url?: string; login?: string; password?: string }>({});
  const [forwardEmail, setForwardEmail] = useState('');
  const [freshDays, setFreshDays] = useState(2);
  const [newType, setNewType] = useState<string | undefined>(undefined);
  const [newBrand, setNewBrand] = useState('');
  const [newModel, setNewModel] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [dragTypeIdx, setDragTypeIdx] = useState<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragModelIdx, setDragModelIdx] = useState<number | null>(null);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [catalogDrawerId, setCatalogDrawerId] = useState<number | null>(null);
  const [catalogItem, setCatalogItem] = useState<CatalogItem | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [hoverProductId, setHoverProductId] = useState<number | null>(null);
  const tasks = useTaskStore(s => s.tasks);
  const activeImportSupplierIds = new Set(
    tasks
      .filter(t =>
        (t.type === 'oil_import_neva' || t.type === 'oil_import_file')
        && (t.status === 'pending' || t.status === 'running')
      )
      .map(t => t.result_json?.supplier_id as number | undefined)
      .filter((id): id is number => id !== undefined)
  );
  const hasActiveNevaImport = tasks.some(
    t => t.type === 'oil_import_neva' && (t.status === 'pending' || t.status === 'running')
  );
  useEffect(() => { loadCart(); }, [loadCart]);

  // Автообновление при завершении фонового импорта
  const prevActiveRef = useRef(activeImportSupplierIds);
  useEffect(() => {
    const prev = prevActiveRef.current;
    prevActiveRef.current = activeImportSupplierIds;
    // Если раньше были активные импорты, а теперь стало меньше — импорт завершился
    if (prev.size > 0 && activeImportSupplierIds.size < prev.size) {
      loadData();
    }
  }, [activeImportSupplierIds]);

  // Замерить высоту sticky-баров
  useEffect(() => {
    if (!cartBarRef.current) return;
    const el = cartBarRef.current;
    const ro = new ResizeObserver(() => setCartBarHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (!filterBarRef.current) return;
    const el = filterBarRef.current;
    const ro = new ResizeObserver(() => setFilterBarHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const originRowKey = (productId: number, sizeId: number, origin: string | null) =>
    `${productId}::${sizeId}::${origin ?? '__null__'}`;
  const sizeRowKey = (productId: number, sizeId: number) =>
    `${productId}::${sizeId}`;

  // Автоматически добавлять ключ в selectedRowKeys при наличии товара в корзине
  useEffect(() => {
    const keysWithItems = cartItems
      .filter(i => i.qty > 0)
      .map(i => {
        const prod = products.find(p => p.sizes.some(s => s.id === i.size_id));
        if (!prod) return null;
        return sizeRowKey(prod.id, i.size_id);
      })
      .filter((k, idx, arr): k is string => k !== null && arr.indexOf(k) === idx);
    setSelectedRowKeys(prev => {
      const next = [...prev];
      for (const k of keysWithItems) {
        if (!next.includes(k)) next.push(k);
      }
      return next;
    });
  }, [cartItems, products]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        oilOrdersApi.getSuppliers(),
        oilOrdersApi.getProducts({ search: search || undefined, product_type: filterType }),
      ]);
      setSuppliers(s);
      setProducts(p);
    } finally {
      setLoading(false);
    }
  }, [search, filterType]);

  useEffect(() => { loadData(); }, [loadData]);

  const prevNevaActiveRef = useRef(hasActiveNevaImport);
  useEffect(() => {
    const wasActive = prevNevaActiveRef.current;
    prevNevaActiveRef.current = hasActiveNevaImport;
    if (wasActive && !hasActiveNevaImport) {
      loadData();
    }
  }, [hasActiveNevaImport, loadData]);

  // Загрузка настроек
  const loadSettings = useCallback(async () => {
    try {
      const s = await oilOrdersApi.getSettings();
      setTypeOrder(s.type_order || []);
      setBrandOrder(s.brand_order || []);
      setModelOrder(s.model_order || []);
      setNevaConfig(s.neva_config || {});
      setForwardEmail(s.forward_email || '');
      setFreshDays(s.fresh_days ?? 2);
    } catch {}
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Загрузка каталога типов товаров (для выпадающего списка в настройках)
  useEffect(() => {
    getProductTypeCatalog()
      .then(setProductTypes)
      .catch(() => {});
  }, []);

  const saveSettings = async () => {
    setSettingsLoading(true);
    try {
      const s = await oilOrdersApi.updateSettings({ type_order: typeOrder, brand_order: brandOrder, model_order: modelOrder, neva_config: nevaConfig, forward_email: forwardEmail, fresh_days: freshDays });
      setTypeOrder(s.type_order || []);
      setBrandOrder(s.brand_order || []);
      setModelOrder(s.model_order || []);
      setNevaConfig(s.neva_config || {});
      setForwardEmail(s.forward_email || '');
      setFreshDays(s.fresh_days ?? 2);
      message.success('Настройки сохранены');
      setSettingsOpen(false);
      loadData(); // перезагрузить продукты с новой сортировкой
    } catch {
      message.error('Ошибка сохранения настроек');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Загрузить список контрагентов (один раз)
  useEffect(() => {
    api.get('/counterparties?limit=200').then(r => {
      const items = Array.isArray(r.data) ? r.data : (r.data.items ?? r.data.data ?? []);
      setCounterparties(items.map((c: any) => ({
        id: c.id,
        label: c.short_name || c.name || `#${c.id}`,
        email: c.email ?? null,
      })));
    }).catch(() => {});
  }, []);

  const handleLink = async (supplierId: number, email: string) => {
    setLinking(true);
    try {
      await oilOrdersApi.updateSupplier(supplierId, { email: email || null });
      const s = await oilOrdersApi.getSuppliers();
      setSuppliers(s);
      message.success('Сохранено');
    } catch {
      message.error('Ошибка при сохранении');
    } finally {
      setLinking(false);
    }
  };

  const handleFileDrop = (supplierId: number, file: File) => {
    const s = suppliers.find(x => x.id === supplierId);
    if (!s) return;
    setUploadModal({ supplierId, supplierName: s.name, file });
  };

  const handleUpload = async (fullReplace: boolean) => {
    if (!uploadModal) return;
    setUploading(true);
    try {
      await oilOrdersApi.uploadPricelist(uploadModal.supplierId, uploadModal.file, fullReplace);
      message.info(`${uploadModal.supplierName}: импорт запущен`);
      setUploadModal(null);
      // Suppliers list обновится автоматически при следующем poll задач
      const s = await oilOrdersApi.getSuppliers();
      setSuppliers(s);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка при загрузке файла');
    } finally {
      setUploading(false);
    }
  };

  // ─── Handlers ───
  const refreshSuppliersRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedRefreshSuppliers = useCallback(() => {
    if (refreshSuppliersRef.current) clearTimeout(refreshSuppliersRef.current);
    refreshSuppliersRef.current = setTimeout(async () => {
      const s = await oilOrdersApi.getSuppliers();
      setSuppliers(s);
    }, 500);
  }, []);

  const handleRequest = async (supplierId: number, type: 'full' | 'partial') => {
    setRequesting(prev => new Set(prev).add(supplierId));
    try {
      const result = await oilOrdersApi.requestPricelist(supplierId, {
        type,
        size_ids: type === 'partial'
          ? Array.from(new Set(
              selectedRowKeys.map(k => {
                const parts = k.split('::');
                return parseInt(parts[1]);
              })
            ))
          : [],
      });
      message.success(`Запрос отправлен на ${result.sent_to}`);
      debouncedRefreshSuppliers();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка при отправке запроса');
    } finally {
      setRequesting(prev => { const next = new Set(prev); next.delete(supplierId); return next; });
    }
  };

  const handleFetchNeva = async (supplierId: number) => {
    setRequesting(prev => new Set(prev).add(supplierId));
    try {
      await oilOrdersApi.fetchNeva();
      message.info('НЕВА: импорт запущен, обновляется...');
      loadData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка загрузки НЕВА');
    } finally {
      setRequesting(prev => { const next = new Set(prev); next.delete(supplierId); return next; });
    }
  };

  const handleClearPrices = (supplierId: number) => {
    const sup = suppliers.find(s => s.id === supplierId);
    Modal.confirm({
      title: `Очистить прайс «${sup?.name}»?`,
      content: 'Все цены этого поставщика будут удалены.',
      okText: 'Очистить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await oilOrdersApi.clearPrices(supplierId);
          message.success('Прайс очищен');
          await loadData();
        } catch {
          message.error('Ошибка при очистке прайса');
        }
      },
    });
  };

  const handleCheckEmails = async () => {
    setCheckingEmails(true);
    try {
      const result = await oilOrdersApi.checkEmails();
      if (result.imported > 0) {
        message.success(`Импортировано прайсов: ${result.imported}`);
        await loadData();
      } else {
        message.info(`Проверено писем: ${result.checked}, ничего нового`);
      }
      if (result.details.length > 0) {
        notification.open({
          message: 'Результат проверки почты',
          description: result.details.join('\n'),
          duration: 8,
        });
      }
    } catch {
      message.error('Ошибка при проверке почты');
    } finally {
      setCheckingEmails(false);
    }
  };


  // Парсинг числа из container_size и определение шага кнопок
  const parseContainerVolume = (cs: string): number => {
    const m = cs.match(/([\d.,]+)/);
    if (!m) return 999;
    return parseFloat(m[1].replace(',', '.'));
  };

  const getStepButtons = (cs: string): number[] => {
    const vol = parseContainerVolume(cs);
    if (vol <= 1) return [-12, 12, 0];
    if (vol <= 5) return [-4, 4, 0];
    return [-1, 1, 0];
  };

  // ─── Table rows ───
  interface TableRow {
    key: string;
    sizeId: number;
    origin: string | null;
    productId: number;
    catalogId: number | null;
    product_types: string[];
    brand: string;
    model_name: string;
    container_size: string;
    rowSpan: number;
    taraRowSpan: number;
    prices: { [supplierId: number]: { qty: number | null; ordered: number; price: number; warehouse?: string | null; origin?: string | null } };
    // Виртуальные строки заказа
    _orderType?: 'order-table';
    _parentSizeId?: number;
    _allOriginPrices?: { origin: string | null; prices: TableRow['prices'] }[];
    _containerSize?: string;
  }

  // Уникальные бренды для фильтра — порядок из настроек, остальные по алфавиту
  const allBrands = Array.from(new Set(products.map(p => p.brand))).sort();
  const effectiveBrandOrder = brandOrder.length > 0 ? brandOrder : ['Mobil', 'ZIC'];
  const priorityBrands = effectiveBrandOrder.filter(b => allBrands.includes(b));
  const restBrands = allBrands.filter(b => !effectiveBrandOrder.includes(b));
  const brandOptions = [...priorityBrands, ...restBrands].map(b => ({ value: b, label: b }));

  // Фильтрация по бренду на клиенте
  const filteredProducts = filterBrand ? products.filter(p => p.brand === filterBrand) : products;

  // Сбрасывать пагинацию при изменении фильтров
  useEffect(() => { setCurrentPage(1); }, [filterBrand, filterType, search]);

  const rows: TableRow[] = [];
  for (const product of filteredProducts) {
    // Группировка по размеру → по origin
    type OriginEntry = {
      originKey: string;
      origin: string | null;
      prices: TableRow['prices'];
      isSelected: boolean;
    };
    const sizeOriginRows: { size: typeof product.sizes[0]; origins: OriginEntry[]; originSplit: { origin: string | null; prices: TableRow['prices'] }[] }[] = [];

    for (const size of product.sizes) {
      // Проверка: есть ли хоть у одного поставщика 2+ origin для этого размера.
      // Если нет — сворачиваем все цены в одну строку, сохраняя origin per-supplier.
      // Если да — разбиваем по origin (как раньше), чтобы не слить цены одного поставщика.
      const supplierOrigins: { [sid: number]: Set<string> } = {};
      for (const p of size.prices) {
        const key = p.origin ?? '__null__';
        if (!supplierOrigins[p.supplier_id]) supplierOrigins[p.supplier_id] = new Set();
        supplierOrigins[p.supplier_id].add(key);
      }
      const anySupplierMultiOrigin = Object.values(supplierOrigins).some(s => s.size > 1);

      const byOrigin: { [key: string]: TableRow['prices'] } = {};
      if (anySupplierMultiOrigin) {
        for (const p of size.prices) {
          const key = p.origin ?? '__null__';
          if (!byOrigin[key]) byOrigin[key] = {};
          byOrigin[key][p.supplier_id] = { qty: p.qty, ordered: p.ordered, price: p.price, warehouse: p.warehouse, origin: p.origin };
        }
      } else {
        byOrigin['__null__'] = {};
        for (const p of size.prices) {
          byOrigin['__null__'][p.supplier_id] = { qty: p.qty, ordered: p.ordered, price: p.price, warehouse: p.warehouse, origin: p.origin };
        }
      }

      const originKeys = Object.keys(byOrigin);
      const origins: OriginEntry[] = originKeys.length === 0
        ? [{ originKey: '__null__', origin: null, prices: {}, isSelected: false }]
        : originKeys.map(key => {
            // В collapsed-режиме row.origin = null, а origin каждого поставщика — в prices[sid].origin
            const origin = anySupplierMultiOrigin && key !== '__null__' ? key : null;
            return {
              originKey: key,
              origin,
              prices: byOrigin[key],
              isSelected: false,
            };
          });

      // originSplit — всегда разбиваем по origin (для виртуальной строки заказа,
      // т.к. корзина хранит позиции по (sizeId, supplierId, origin))
      const originSplitMap: { [key: string]: TableRow['prices'] } = {};
      for (const p of size.prices) {
        const key = p.origin ?? '__null__';
        if (!originSplitMap[key]) originSplitMap[key] = {};
        originSplitMap[key][p.supplier_id] = { qty: p.qty, ordered: p.ordered, price: p.price, warehouse: p.warehouse, origin: p.origin };
      }
      const originSplit = Object.keys(originSplitMap).map(key => ({
        origin: key === '__null__' ? null : key,
        prices: originSplitMap[key],
      }));

      sizeOriginRows.push({ size, origins, originSplit });
    }

    // Подсчёт общего rowSpan продукта (выбор на уровне size, не origin)
    let totalProductRows = 0;
    for (const sg of sizeOriginRows) {
      const sRk = sizeRowKey(product.id, sg.size.id);
      const isSizeSelected = selectedRowKeys.includes(sRk) || hasItemsForSize(sg.size.id);
      totalProductRows += sg.origins.length + (isSizeSelected ? 1 : 0);
    }

    let productFirstRow = true;

    for (const sg of sizeOriginRows) {
      const sRk = sizeRowKey(product.id, sg.size.id);
      const isSizeSelected = selectedRowKeys.includes(sRk) || hasItemsForSize(sg.size.id);
      // rowSpan для ячейки "Тара" = origin-строки + виртуальная строка если size выбран
      const taraRowSpanVal = sg.origins.length + (isSizeSelected ? 1 : 0);

      sg.origins.forEach((or, originIdx) => {
        const rk = originRowKey(product.id, sg.size.id, or.origin);

        const dataRow: TableRow = {
          key: rk,
          sizeId: sg.size.id,
          origin: or.origin,
          productId: product.id,
          catalogId: product.catalog_id ?? null,
          product_types: product.product_types,
          brand: product.brand,
          model_name: product.model_name,
          container_size: sg.size.container_size,
          rowSpan: productFirstRow ? totalProductRows : 0,
          taraRowSpan: originIdx === 0 ? taraRowSpanVal : 0,
          prices: or.prices,
        };
        rows.push(dataRow);
        productFirstRow = false;
      });

      // Одна виртуальная строка после всех origins размера
      if (isSizeSelected) {
        rows.push({
          key: `order-t-${sRk}`,
          sizeId: sg.size.id,
          productId: product.id,
          catalogId: null,
          origin: null,
          product_types: product.product_types || [], brand: product.brand || '', model_name: product.model_name || '',
          container_size: sg.size.container_size || '',
          rowSpan: 0,
          taraRowSpan: 0,
          prices: {},
          _orderType: 'order-table',
          _parentSizeId: sg.size.id,
          _allOriginPrices: sg.originSplit,
          _containerSize: sg.size.container_size,
        });
      }
    }
  }

  // ─── Пагинация по продуктам (чтобы rowSpan-группы не разрезались) ───
  const uniqueProductIds = Array.from(new Set(rows.filter(r => !r._orderType).map(r => r.productId)));
  const totalProducts = uniqueProductIds.length;
  const pageProductIds = new Set(uniqueProductIds.slice((currentPage - 1) * pageSize, currentPage * pageSize));
  const pagedRows = rows.filter(r => pageProductIds.has(r.productId));

  // Ключи первых строк СЛЕДУЮЩЕГО продукта после активного блока (нижняя горизонтальная граница)
  // Нельзя брать просто следующую строку — она может быть ещё внутри той же группы (другой размер)
  // и не иметь Бренд/Тип/Модель TD (rowSpan=0). Нужна первая строка другого продукта.
  const afterButtonsSet = new Set<string>();
  for (let i = 0; i < pagedRows.length - 1; i++) {
    if (pagedRows[i]._orderType === 'order-table') {
      const productId = pagedRows[i].productId;
      for (let j = i + 1; j < pagedRows.length; j++) {
        if (pagedRows[j].productId !== productId) {
          afterButtonsSet.add(pagedRows[j].key);
          break;
        }
      }
    }
  }

  // При выборе новой строки — убрать из selectedRowKeys те, где всё по нулям
  const openCatalogDrawer = useCallback(async (catalogId: number) => {
    setCatalogDrawerId(catalogId);
    setCatalogLoading(true);
    setCatalogItem(null);
    try {
      const item = await getCatalogItem(catalogId);
      setCatalogItem(item);
    } catch { /* ignore */ }
    setCatalogLoading(false);
  }, []);

  const toggleRow = (productId: number, sizeId: number) => {
    if (hasItemsForSize(sizeId)) return;
    const rk = sizeRowKey(productId, sizeId);
    setSelectedRowKeys(prev => {
      if (prev.includes(rk)) return prev.filter(k => k !== rk);
      // Выбираем новую — сворачиваем пустые
      const cleaned = prev.filter(k => {
        const parts = k.split('::');
        const sid = parseInt(parts[1]);
        return hasItemsForSize(sid);
      });
      return [...cleaned, rk];
    });
  };

  // Хелпер для объединённых ячеек: по Y-позиции курсора определяет нужную подстроку
  const spannedOnCell = (row: TableRow) => {
    if (row.rowSpan <= 1) return { rowSpan: row.rowSpan };
    const groupRows = pagedRows.filter(r => r.productId === row.productId && !r._orderType);
    const getTarget = (e: React.MouseEvent<HTMLElement>): TableRow | null => {
      const rect = e.currentTarget.getBoundingClientRect();
      const relY = e.clientY - rect.top;
      const rowH = rect.height / row.rowSpan;
      const idx = Math.min(Math.max(0, Math.floor(relY / rowH)), row.rowSpan - 1);
      return groupRows[idx] ?? null;
    };
    return {
      rowSpan: row.rowSpan,
      className: 'oil-merged-cell',  // подавляем Ant Design hover на объединённой ячейке
      style: { cursor: 'pointer' },
      onMouseMove: (e: React.MouseEvent<HTMLElement>) => {
        const t = getTarget(e);
        if (t) {
          const tSRk = sizeRowKey(t.productId, t.sizeId);
          if (tSRk !== hoverRowKey) setHoverRowKey(tSRk);
        }
      },
      onMouseLeave: () => setHoverRowKey(null),
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        const t = getTarget(e);
        if (t) toggleRow(t.productId, t.sizeId);
      },
    };
  };

  const taraOnCell = (row: TableRow) => {
    if (row._orderType) return { rowSpan: 0 };
    if (row.taraRowSpan === 0) return { rowSpan: 0 };
    if (row.taraRowSpan === 1) return { rowSpan: 1, className: 'oil-merged-cell oil-tara-cell' };
    const sizeRows = pagedRows.filter(r => r.productId === row.productId && r.sizeId === row.sizeId && !r._orderType);
    const getTarget = (e: React.MouseEvent<HTMLElement>): TableRow | null => {
      const rect = e.currentTarget.getBoundingClientRect();
      const relY = e.clientY - rect.top;
      const rowH = rect.height / row.taraRowSpan;
      const idx = Math.min(Math.max(0, Math.floor(relY / rowH)), sizeRows.length - 1);
      return sizeRows[idx] ?? null;
    };
    return {
      rowSpan: row.taraRowSpan,
      className: 'oil-merged-cell oil-tara-cell',
      style: { cursor: 'pointer' },
      onMouseMove: (e: React.MouseEvent<HTMLElement>) => {
        const t = getTarget(e);
        if (t) {
          const tSRk = sizeRowKey(t.productId, t.sizeId);
          if (tSRk !== hoverRowKey) setHoverRowKey(tSRk);
        }
      },
      onMouseLeave: () => setHoverRowKey(null),
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        const t = getTarget(e);
        if (t) toggleRow(t.productId, t.sizeId);
      },
    };
  };

  // ─── Column definitions ───
  const PALETTE = [
    { bg: '#f0f7ff', border: '#ddeaf6' },
    { bg: '#f2f9f2', border: '#d4ead2' },
    { bg: '#f5f0fb', border: '#e0d8f0' },
    { bg: '#fdf5f0', border: '#ede0d8' },
  ];

  const catalogLink = (val: any, row: TableRow) => {
    if (row._orderType) return null;
    if (!val) return null;
    if (!row.catalogId) return val;
    return <span className={`oil-catalog-link${hoverProductId === row.productId ? ' oil-catalog-hover' : ''}`} onMouseEnter={() => setHoverProductId(row.productId)} onMouseLeave={() => setHoverProductId(null)} onClick={(e) => { e.stopPropagation(); openCatalogDrawer(row.catalogId!); }}>{val}</span>;
  };

  function renderProductTypes(types: string[] | null | undefined) {
    if (!types || types.length === 0) return <span>—</span>;
    return <span style={{ whiteSpace: 'pre-line' }}>{types.join(',\n')}</span>;
  }

  const brandWithLogo = (val: any, row: TableRow) => {
    if (row._orderType) return null;
    if (!val) return null;
    const content = <BrandLogo brand={val} />;
    if (!row.catalogId) return content;
    return (
      <span
        className={`oil-catalog-link${hoverProductId === row.productId ? ' oil-catalog-hover' : ''}`}
        onMouseEnter={() => setHoverProductId(row.productId)}
        onMouseLeave={() => setHoverProductId(null)}
        onClick={(e) => { e.stopPropagation(); openCatalogDrawer(row.catalogId!); }}
      >
        {content}
      </span>
    );
  };

  const staticDefs: Record<string, any> = {
    brand: {
      title: 'Бренд', dataIndex: 'brand', width: 140,
      onHeaderCell: () => ({ columnKey: 'brand' }),
      onCell: (row: TableRow) => { if (row._orderType) return { colSpan: 0 }; return spannedOnCell(row); },
      render: brandWithLogo,
    },
    type: {
      title: 'Тип', dataIndex: 'product_types', width: 100,
      onHeaderCell: () => ({ columnKey: 'type' }),
      onCell: (row: TableRow) => { if (row._orderType) return { colSpan: 0 }; return spannedOnCell(row); },
      render: (_: any, record: TableRow) => record._orderType ? null : renderProductTypes(record.product_types),
    },
    model: {
      title: 'Модель', dataIndex: 'model_name',
      onHeaderCell: () => ({ columnKey: 'model' }),
      onCell: (row: TableRow) => { if (row._orderType) return { colSpan: 0 }; return spannedOnCell(row); },
      render: catalogLink,
    },
    tara: {
      title: 'Тара', dataIndex: 'container_size', width: 80,
      onHeaderCell: () => ({ columnKey: 'tara' }),
      onCell: (row: TableRow) => taraOnCell(row),
      render: (val: any, row: TableRow) => { if (row._orderType || row.taraRowSpan === 0) return null; return val; },
    },
  };

  const supplierKeysInOrder = columnOrder.filter(k => k.startsWith('supplier:'));
  const totalSupplierSubCols = supplierKeysInOrder.length * 3;

  function buildSupplierCol(s: OilSupplier) {
    const supplierKey = `supplier:${s.id}`;
    const visualIdx = supplierKeysInOrder.indexOf(supplierKey);
    const isFirst = visualIdx === 0;
    // Цвет привязан к ID поставщика (стабильный при перемещении)
    const sIdx = suppliers.findIndex(x => x.id === s.id);
    const color = PALETTE[(sIdx >= 0 ? sIdx : 0) % PALETTE.length];
    const borderStyle = `3px solid ${color.border}`;
    return {
      title: s.name,
      key: `supplier_${s.id}`,
      onHeaderCell: () => ({ columnKey: supplierKey, className: 'oil-supplier-header', style: { background: color.bg, borderLeft: borderStyle } }),
      children: [
    {
      title: 'Пр.',
      key: `origin_${s.id}`,
      width: 48,
      align: 'center' as const,
      onHeaderCell: () => ({ style: { borderLeft: borderStyle, fontSize: 11, padding: '4px 2px', whiteSpace: 'nowrap' } }),
      onCell: (row: TableRow) => {
        if (row._orderType) {
          return isFirst ? { colSpan: totalSupplierSubCols } : { colSpan: 0 };
        }
        return { style: { borderLeft: borderStyle, padding: '4px 2px' } };
      },
      render: (_: any, row: TableRow) => {
        if (row._orderType === 'order-table') {
          if (!isFirst) return null;
          return (
            <OrderSubTable
              sizeId={row._parentSizeId!}
              containerSize={row._containerSize ?? ''}
              allOriginPrices={row._allOriginPrices!}
              suppliers={suppliers}
              getQty={getQty}
              setQty={setQty}
              brand={row.brand}
              productType={row.product_types?.[0] ?? ''}
              modelName={row.model_name}
              columnOrder={columnOrder}
              onAllZero={() => {
                const rk = sizeRowKey(row.productId, row._parentSizeId!);
                setSelectedRowKeys(prev => prev.filter(k => k !== rk));
              }}
            />
          );
        }
        if (row._orderType) return null;
        const p = row.prices[s.id];
        if (!p) return null;
        const origin = p.origin ?? row.origin;
        if (!origin) return null;
        const tagColor = origin === 'EU' ? 'blue' : origin === 'TR' ? 'orange' : 'default';
        return <Tag color={tagColor} style={{ fontSize: 10, margin: 0, padding: '0 4px' }}>{origin}</Tag>;
      },
    },
      {
        title: 'Кол-во',
        key: `qty_${s.id}`,
        width: 80,
        align: 'right' as const,
        onHeaderCell: () => ({}),
        onCell: (row: TableRow) => {
          if (row._orderType) return { colSpan: 0 };
          return {};
        },
        render: (_: any, row: TableRow) => {
          if (row._orderType) return null;

          // Обычная data-строка: остаток/всего
          const p = row.prices[s.id];
          if (!p) return null;
          const inCart = getQty(row.sizeId, s.id, p.origin ?? row.origin);

          // НЕВА: показываем склад вместо числа
          if (p.warehouse) {
            const warehouseLabels: Record<string, string> = {
              'мурманск': 'Склад Мурм.',
              'спб': 'Склад СПб',
              'общий': 'Склад общий',
              'заказ': 'Заказ',
            };
            const label = warehouseLabels[p.warehouse] || p.warehouse;
            const taken = p.ordered + inCart;
            if (taken > 0) return <span style={{ fontSize: 12 }}>{label} ({'\u2212'}{taken})</span>;
            return <span style={{ fontSize: 12 }}>{label}</span>;
          }

          // qty=null → количество неизвестно (fallback)
          if (p.qty == null) {
            if (p.ordered > 0 || inCart > 0) return `? (−${p.ordered + inCart})`;
            return '?';
          }
          const remaining = p.qty - p.ordered - inCart;
          if (p.ordered > 0 || inCart > 0) {
            if (remaining < 0) {
              return <span style={{ color: '#ff4d4f', fontWeight: 600 }}>⚠ {remaining}/{p.qty}</span>;
            }
            return `${remaining}/${p.qty}`;
          }
          return p.qty;
        },
      },
      {
        title: 'Цена',
        key: `price_${s.id}`,
        width: 116,
        align: 'right' as const,
        onCell: (row: TableRow) => {
          if (row._orderType) return { colSpan: 0 };
          return { style: { whiteSpace: 'nowrap' } };
        },
        render: (_: any, row: TableRow) => {
          if (row._orderType) return null;

          // Обычная data-строка
          const p = row.prices[s.id];
          return p ? `${p.price.toLocaleString('ru')} ₽` : null;
        },
      },
        ],
    };
  }

  // Assemble columns in saved order
  const columns: any[] = columnOrder.map(key => {
    if (staticDefs[key]) return staticDefs[key];
    if (key.startsWith('supplier:')) {
      const sid = parseInt(key.split(':')[1]);
      const s = suppliers.find(x => x.id === sid);
      if (s) return buildSupplierCol(s);
    }
    return null;
  }).filter(Boolean);


  return (
    <div style={{ padding: 24 }}>
      <style>{`
        /* Разделитель поставщиков */
        th.oil-supplier-header { font-weight: 600 !important; }
        /* Подавляем ховер Ant Design на объединённых ячейках (он подсвечивает их при наведении на любую строку в rowspan) */
        td.oil-merged-cell.ant-table-cell-row-hover { background: #fff !important; }
        /* Когда строка выделена — объединённая ячейка должна показывать выделение */
        tr.ant-table-row-selected > td.oil-merged-cell.ant-table-cell-row-hover { background: #e6f4ff !important; }
        /* Подавить ховер на строках вне целевой */
        tr.oil-suppress-hover > td,
        tr.oil-suppress-hover:hover > td,
        tr.oil-suppress-hover > td.ant-table-cell-row-hover { background: #fff !important; }
        tr.oil-suppress-hover.ant-table-row-selected > td,
        tr.oil-suppress-hover.ant-table-row-selected:hover > td,
        tr.oil-suppress-hover.ant-table-row-selected > td.ant-table-cell-row-hover { background: #e6f4ff !important; }
        /* Виртуальный ховер — только целевая строка */
        tr.oil-virtual-hover > td,
        tr.oil-virtual-hover:hover > td,
        tr.oil-virtual-hover > td.ant-table-cell-row-hover { background: #f5f5f5 !important; }
        tr.oil-virtual-hover.ant-table-row-selected > td,
        tr.oil-virtual-hover.ant-table-row-selected:hover > td,
        tr.oil-virtual-hover.ant-table-row-selected > td.ant-table-cell-row-hover { background: #bae0ff !important; }
        /* Хедер группы при ховере (цель НЕ выбрана): merged ячейки — серые (тара исключена — она привязана к размеру, не к продукту) */
        tr.oil-group-hover > td.oil-merged-cell:not(.oil-tara-cell),
        tr.oil-group-hover > td.oil-merged-cell:not(.oil-tara-cell).ant-table-cell-row-hover { background: #f5f5f5 !important; }
        tr.oil-group-hover > td:not(.oil-merged-cell),
        tr.oil-group-hover > td.oil-tara-cell,
        tr.oil-group-hover > td:not(.oil-merged-cell).ant-table-cell-row-hover,
        tr.oil-group-hover > td.oil-tara-cell.ant-table-cell-row-hover { background: #fff !important; }
        tr.oil-group-hover.ant-table-row-selected > td.oil-merged-cell:not(.oil-tara-cell) { background: #bae0ff !important; }
        tr.oil-group-hover.ant-table-row-selected > td:not(.oil-merged-cell),
        tr.oil-group-hover.ant-table-row-selected > td.oil-tara-cell { background: #e6f4ff !important; }
        /* Хедер группы при ховере (цель выбрана): merged ячейки — синие (тара исключена) */
        tr.oil-group-hover-sel > td.oil-merged-cell:not(.oil-tara-cell),
        tr.oil-group-hover-sel > td.oil-merged-cell:not(.oil-tara-cell).ant-table-cell-row-hover { background: #bae0ff !important; }
        tr.oil-group-hover-sel > td:not(.oil-merged-cell),
        tr.oil-group-hover-sel > td.oil-tara-cell,
        tr.oil-group-hover-sel > td:not(.oil-merged-cell).ant-table-cell-row-hover,
        tr.oil-group-hover-sel > td.oil-tara-cell.ant-table-cell-row-hover { background: #fff !important; }
        tr.oil-group-hover-sel.ant-table-row-selected > td.oil-merged-cell:not(.oil-tara-cell) { background: #bae0ff !important; }
        tr.oil-group-hover-sel.ant-table-row-selected > td:not(.oil-merged-cell),
        tr.oil-group-hover-sel.ant-table-row-selected > td.oil-tara-cell { background: #e6f4ff !important; }
        /* Хедер группы при выборе сиблинга (без ховера): merged ячейки — синие (тара исключена) */
        tr.oil-group-selected > td.oil-merged-cell:not(.oil-tara-cell) { background: #e6f4ff !important; }
        tr.oil-group-selected > td:not(.oil-merged-cell),
        tr.oil-group-selected > td.oil-tara-cell { background: #fff !important; }
        /* Виртуальные строки заказа */
        tr.oil-order-row > td { background: #e6f4ff !important; border-bottom: 1px solid #bae0ff !important; }
        tr.oil-order-row:hover > td { background: #e6f4ff !important; }
        tr.oil-order-row > td.ant-table-cell-row-hover { background: #e6f4ff !important; }
        /* Горизонтальные границы всего активного блока — сначала, до рамки заказа */
        tr.oil-block-start > td { border-top: 2px solid #91caff !important; }
        /* Нижняя граница: border-top на первой строке после order-buttons — она имеет все TD = полная ширина */
        tr.oil-after-order > td { border-top: 2px solid #91caff !important; }
        /* Строка-контейнер вложенной таблицы */
        tr.oil-order-order-table > td { background: #f5f8ff !important; padding: 8px 10px !important; overflow: visible !important; }
        tr.oil-order-order-table:hover > td { background: #f5f8ff !important; }
        tr.oil-order-order-table > td.ant-table-cell-row-hover { background: #f5f8ff !important; }
        /* Активная строка во вложенной таблице */
        tr.oil-subtable-active > td { background: #e6f4ff !important; }
        /* Подсветка ячеек с товаром в корзине */
        tr.oil-order-row > td.oil-cart-active,
        tr.oil-order-row:hover > td.oil-cart-active,
        tr.oil-order-row > td.oil-cart-active.ant-table-cell-row-hover { background: #bae0ff !important; }
        /* Ссылки на карточку каталога */
        .oil-catalog-link { cursor: pointer; color: inherit; }
        .oil-catalog-link.oil-catalog-hover { text-decoration: underline; }
      `}</style>
      {/* Sticky-бар: заголовок + корзина/история */}
      {(() => {
        const totalQty = cartItems.reduce((sum, i) => sum + i.qty, 0);
        let totalSum = 0;
        for (const ci of cartItems) {
          for (const prod of products) {
            const size = prod.sizes.find(s => s.id === ci.size_id);
            if (size) {
              const price = size.prices.find(p => p.supplier_id === ci.supplier_id && p.origin === ci.origin);
              if (price) totalSum += ci.qty * price.price;
              break;
            }
          }
        }
        // Строки попапа корзины
        const cartPopupRows = cartItems
          .filter(ci => ci.qty > 0)
          .map(ci => {
            const supplier = suppliers.find(s => s.id === ci.supplier_id);
            let prod: OilProduct | undefined;
            let sizeObj: OilProduct['sizes'][0] | undefined;
            for (const p of products) {
              const sz = p.sizes.find(s => s.id === ci.size_id);
              if (sz) { prod = p; sizeObj = sz; break; }
            }
            const price = sizeObj?.prices.find(p => p.supplier_id === ci.supplier_id && p.origin === ci.origin);
            const sum = price ? ci.qty * price.price : 0;
            return {
              key: `${ci.size_id}::${ci.supplier_id}::${ci.origin ?? ''}`,
              supplier: supplier?.name ?? '—',
              brand: prod?.brand ?? '—',
              type: prod?.product_types?.[0] ?? '—',
              model: prod?.model_name ?? '—',
              tara: sizeObj?.container_size ?? '—',
              qty: ci.qty,
              sum,
            };
          });

        const cartPopoverContent = (
          <div style={{ width: 640 }}>
            <style>{`
              .oil-cart-popup .ant-table-thead > tr > th {
                font-size: 10px !important;
                padding: 3px 6px !important;
                background: #fafafa !important;
                white-space: nowrap;
              }
              .oil-cart-popup .ant-table-tbody > tr > td {
                font-size: 11px !important;
                padding: 3px 6px !important;
              }
              .oil-cart-popup .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
            `}</style>
            <div style={{ maxHeight: 185, overflowY: 'auto' }} className="oil-cart-popup">
              <Table
                size="small"
                dataSource={cartPopupRows}
                pagination={false}
                columns={[
                  { title: 'Поставщик', dataIndex: 'supplier', key: 'supplier', width: 90, ellipsis: true },
                  { title: 'Бренд', dataIndex: 'brand', key: 'brand', width: 80, ellipsis: true,
                    render: (b: string) => <BrandLogo brand={b} /> },
                  { title: 'Тип', dataIndex: 'type', key: 'type', width: 90, ellipsis: true },
                  { title: 'Наименование', dataIndex: 'model', key: 'model', ellipsis: true },
                  { title: 'Тара', dataIndex: 'tara', key: 'tara', width: 60, ellipsis: true },
                  { title: 'Кол-во', dataIndex: 'qty', key: 'qty', width: 52, align: 'right' as const },
                  {
                    title: 'Сумма', dataIndex: 'sum', key: 'sum', width: 90, align: 'right' as const,
                    render: (v: number) => v.toLocaleString('ru', { minimumFractionDigits: 2 }),
                  },
                ]}
              />
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, color: '#888', marginTop: 4, paddingRight: 2 }}>
              Итого: <strong>{totalQty} шт · {totalSum.toLocaleString('ru', { minimumFractionDigits: 2 })} ₽</strong>
            </div>
          </div>
        );

        return (
          <div
            ref={cartBarRef}
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 11,
              background: '#fff',
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0',
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Title level={3} style={{ margin: 0 }}>Закупка масла — импорт</Title>
            <Space split={<span style={{ color: '#d9d9d9' }}>|</span>} size={16}>
              <Button
                type="link"
                onClick={() => navigate('/oil-orders/history')}
                style={{ fontSize: 14, padding: 0 }}
              >
                История заказов
              </Button>
              <Popover
                content={totalQty > 0 ? cartPopoverContent : undefined}
                open={totalQty > 0 ? undefined : false}
                trigger="hover"
                placement="bottomRight"
                overlayStyle={{ maxWidth: 660 }}
                mouseEnterDelay={0.15}
                mouseLeaveDelay={0.1}
              >
              <Button
                type="link"
                icon={<ShoppingCartOutlined />}
                onClick={() => navigate('/oil-orders/cart')}
                style={{ fontSize: 14, padding: 0 }}
              >
                Корзина{totalQty > 0 ? `: ${totalQty} шт · ${totalSum.toLocaleString('ru', { minimumFractionDigits: 2 })} ₽` : ''}
              </Button>
              </Popover>
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={() => setSettingsOpen(true)}
                style={{ fontSize: 16, padding: '0 4px' }}
              />
            </Space>
          </div>
        );
      })()}

      {/* Supplier cards */}
      <Row gutter={16} style={{ marginBottom: 24 }} align="top">
        {suppliers.map(s => (
          <Col key={s.id}>
            <SupplierCard
              supplier={s}
              loading={requesting.has(s.id) || activeImportSupplierIds.has(s.id)}
              activeImport={activeImportSupplierIds.has(s.id)}
              onRequest={(id) => handleRequest(id, 'full')}
              onLink={handleLink}
              linking={linking}
              onDrop={handleFileDrop}
              onCardClick={s.counterparty_id ? (cpId) => setCpDrawer({ open: true, id: cpId }) : undefined}
              onClearPrices={handleClearPrices}
              webUrl={s.source_type === 'web' ? nevaConfig.url : undefined}
              onFetchWeb={s.source_type === 'web' ? handleFetchNeva : undefined}
            />
          </Col>
        ))}
        <Col style={{ alignSelf: 'flex-end' }}>
          <Button loading={checkingEmails} onClick={handleCheckEmails}>
            Проверить почту
          </Button>
        </Col>
      </Row>

      {/* Filters + Pagination */}
      <div ref={filterBarRef} style={{
        position: 'sticky',
        top: cartBarHeight,
        zIndex: 10,
        background: '#fff',
        padding: '4px 0 8px',
        borderBottom: '1px solid #f0f0f0',
        marginBottom: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <Space wrap style={{ flex: 1, minWidth: 0 }}>
          <Select
            placeholder="Тип"
            allowClear
            value={filterType}
            onChange={setFilterType}
            style={{ width: 220 }}
            options={[
              { value: 'масло моторное', label: 'Масло моторное' },
              { value: 'масло АКПП', label: 'Масло АКПП' },
              { value: 'масло МКПП', label: 'Масло МКПП' },
              { value: 'масло трансмиссионное', label: 'Масло трансмиссионное' },
              { value: 'масло гидравлическое', label: 'Масло гидравлическое' },
              { value: 'масло компрессорное', label: 'Масло компрессорное' },
              { value: 'масло индустриальное', label: 'Масло индустриальное' },
              { value: 'масло 2т', label: 'Масло 2Т' },
              { value: 'масло', label: 'Масло (прочее)' },
              { value: 'антифриз', label: 'Антифриз' },
              { value: 'жидкость ГУР', label: 'Жидкость ГУР' },
              { value: 'тормозная жидкость', label: 'Тормозная жидкость' },
              { value: 'жидкость', label: 'Жидкость (прочая)' },
              { value: 'смазка', label: 'Смазка' },
              { value: 'автохимия', label: 'Автохимия' },
              { value: 'аккумулятор', label: 'Аккумулятор' },
              { value: 'прочее', label: 'Прочее' },
            ]}
          />
          <Select
            placeholder="Бренд"
            allowClear
            showSearch
            value={filterBrand}
            onChange={setFilterBrand}
            style={{ width: 180 }}
            options={brandOptions}
            filterOption={(input, opt) =>
              String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Button
            type={filterBrand === 'Mobil' ? 'primary' : 'default'}
            onClick={() => setFilterBrand(filterBrand === 'Mobil' ? undefined : 'Mobil')}
          >
            Mobil
          </Button>
          <Button
            type={filterBrand === 'ZIC' ? 'primary' : 'default'}
            onClick={() => setFilterBrand(filterBrand === 'ZIC' ? undefined : 'ZIC')}
          >
            ZIC
          </Button>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Поиск по модели"
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ width: 240 }}
          />
        </Space>
        {totalProducts > pageSize && (
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalProducts}
            showSizeChanger
            size="small"
            style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        )}
      </div>

      {/* Table with draggable edge handles */}
      <div style={{ position: 'relative', paddingLeft: tablePadding.left, paddingRight: tablePadding.right }}>
        {/* Left drag handle */}
        <div
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: Math.max(tablePadding.left, 6), cursor: 'col-resize', zIndex: 5 }}
          onPointerDown={(e) => {
            e.preventDefault();
            const startX = e.clientX, startW = tablePadding.left;
            const onMove = (ev: PointerEvent) => {
              const next = { ...tablePadding, left: Math.max(0, Math.round(startW + ev.clientX - startX)) };
              setTablePadding(next);
              localStorage.setItem('oil-table-padding', JSON.stringify(next));
            };
            const onUp = () => { document.removeEventListener('pointermove', onMove); document.removeEventListener('pointerup', onUp); };
            document.addEventListener('pointermove', onMove);
            document.addEventListener('pointerup', onUp);
          }}
        />
        {/* Right drag handle */}
        <div
          style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: Math.max(tablePadding.right, 6), cursor: 'col-resize', zIndex: 5 }}
          onPointerDown={(e) => {
            e.preventDefault();
            const startX = e.clientX, startW = tablePadding.right;
            const onMove = (ev: PointerEvent) => {
              const next = { ...tablePadding, right: Math.max(0, Math.round(startW - (ev.clientX - startX))) };
              setTablePadding(next);
              localStorage.setItem('oil-table-padding', JSON.stringify(next));
            };
            const onUp = () => { document.removeEventListener('pointermove', onMove); document.removeEventListener('pointerup', onUp); };
            document.addEventListener('pointermove', onMove);
            document.addEventListener('pointerup', onUp);
          }}
        />
      <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
      <Table
        components={{ header: { cell: DragHeaderCell } }}
        columns={columns}
        dataSource={pagedRows}
        loading={loading}
        size="small"
        pagination={false}
        scroll={{ x: true }}
        sticky={{ offsetHeader: cartBarHeight + filterBarHeight }}
        locale={{ emptyText: 'Нет данных. Запросите прайс у поставщиков.' }}
        rowClassName={(row) => {
          // Виртуальные строки заказа — свой стиль
          if (row._orderType) {
            return `oil-order-row oil-order-${row._orderType}`;
          }

          const sRk = sizeRowKey(row.productId, row.sizeId);
          const isSelected = selectedRowKeys.includes(sRk) || hasItemsForSize(row.sizeId);
          const isGroupHeader = row.rowSpan > 1;

          const classes: string[] = [];
          if (isSelected) classes.push('ant-table-row-selected');

          // Ховер хранится как sizeRowKey (productId::sizeId)
          const hovParts = hoverRowKey !== null ? hoverRowKey.split('::') : null;
          const hovProductId = hovParts ? parseInt(hovParts[0]) : null;
          const hovSizeId = hovParts ? parseInt(hovParts[1]) : null;
          const isHoveringThisGroup = hovProductId !== null && hovProductId === row.productId;
          const isSizeHovered = isHoveringThisGroup && hovSizeId === row.sizeId;

          if (isHoveringThisGroup) {
            if (isSizeHovered) {
              // Все origin-строки этого размера — виртуальный ховер
              classes.push('oil-virtual-hover');
            } else if (isGroupHeader) {
              // Хедер группы: цвет зависит от того, выбран ли наведённый размер
              const targetSelected = hovSizeId !== null &&
                (selectedRowKeys.includes(sizeRowKey(row.productId, hovSizeId)) || hasItemsForSize(hovSizeId));
              classes.push(targetSelected ? 'oil-group-hover-sel' : 'oil-group-hover');
            } else {
              // Прочие строки группы (другой размер) — подавляем
              classes.push('oil-suppress-hover');
            }
          }

          // При выборе строки-сиблинга без ховера — подсвечиваем объединённые ячейки хедера
          if (isGroupHeader) {
            const groupRows = pagedRows.filter(r => r.productId === row.productId && !r._orderType);
            const hasSiblingSelected = groupRows.some(r => {
              const sibSRk = sizeRowKey(r.productId, r.sizeId);
              return r.key !== row.key && (selectedRowKeys.includes(sibSRk) || hasItemsForSize(r.sizeId));
            });
            if (isSelected || hasSiblingSelected) classes.push('oil-block-start');
            if (!isSelected && !isHoveringThisGroup && hasSiblingSelected) {
              classes.push('oil-group-selected');
            }
          }

          // Нижняя горизонтальная граница: строка сразу после order-buttons имеет все TD → полная ширина
          if (afterButtonsSet.has(row.key)) classes.push('oil-after-order');

          return classes.join(' ');
        }}
        onRow={(row) => {
          if (row._orderType) return { style: { cursor: 'default' } };
          return {
            onClick: () => toggleRow(row.productId, row.sizeId),
            onMouseEnter: () => setHoverRowKey(sizeRowKey(row.productId, row.sizeId)),
            onMouseLeave: () => setHoverRowKey(null),
            style: { cursor: 'pointer' },
          };
        }}
      />
        </SortableContext>
      </DndContext>
      </div>

      {/* Bottom pagination */}
      {totalProducts > pageSize && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 0' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalProducts}
            showSizeChanger
            size="small"
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </div>
      )}

      {/* Drawer контрагента */}
      <CounterpartyInfoDrawer
        open={cpDrawer.open}
        onClose={() => setCpDrawer({ open: false, id: null })}
        counterpartyId={cpDrawer.id}
      />

      {/* Карточка товара из каталога */}
      <Drawer
        open={catalogDrawerId !== null}
        onClose={() => { setCatalogDrawerId(null); setCatalogItem(null); }}
        title={catalogItem ? `${catalogItem.brand} — ${catalogItem.model_name}` : 'Карточка товара'}
        width={480}
      >
        {catalogLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : catalogItem ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Бренд">{catalogItem.brand}</Descriptions.Item>
            <Descriptions.Item label="Тип">{renderProductTypes(catalogItem.product_types)}</Descriptions.Item>
            <Descriptions.Item label="Модель">{catalogItem.model_name}</Descriptions.Item>
            {catalogItem.viscosity && <Descriptions.Item label="Вязкость">{catalogItem.viscosity}</Descriptions.Item>}
            {catalogItem.base_type && <Descriptions.Item label="Базовый тип">{catalogItem.base_type}</Descriptions.Item>}
            {catalogItem.specifications && catalogItem.specifications.length > 0 && (
              <Descriptions.Item label="Спецификации">{catalogItem.specifications.join(', ')}</Descriptions.Item>
            )}
            {catalogItem.description && <Descriptions.Item label="Описание">{catalogItem.description}</Descriptions.Item>}
            {catalogItem.extra_data && Object.keys(catalogItem.extra_data).length > 0 && (
              Object.entries(catalogItem.extra_data).map(([k, v]) => (
                <Descriptions.Item key={k} label={k}>{String(v)}</Descriptions.Item>
              ))
            )}
            {catalogItem.ai_enriched && (
              <Descriptions.Item label="AI">
                <Tag color={catalogItem.ai_confidence && catalogItem.ai_confidence >= 0.7 ? 'green' : 'orange'}>
                  AI {catalogItem.ai_confidence ? `${Math.round(catalogItem.ai_confidence * 100)}%` : ''}
                </Tag>
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : null}
      </Drawer>

      {/* Upload modal */}
      <Modal
        open={!!uploadModal}
        title={uploadModal?.supplierName ?? ''}
        onCancel={() => !uploading && setUploadModal(null)}
        footer={null}
        width={360}
        centered
      >
        <Spin spinning={uploading}>
          <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
            <div style={{ marginBottom: 16, color: '#666' }}>
              <InboxOutlined style={{ fontSize: 28, marginRight: 8, color: '#1890ff' }} />
              <Typography.Text>{uploadModal?.file.name}</Typography.Text>
            </div>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                block
                onClick={() => handleUpload(true)}
                disabled={uploading}
              >
                Заменить прайс
              </Button>
              <Button
                block
                danger
                onClick={() => setUploadModal(null)}
                disabled={uploading}
              >
                ✕ Отмена
              </Button>
            </Space>
          </div>
        </Spin>
      </Modal>

      {/* Settings modal */}
      <Modal
        open={settingsOpen}
        title="Настройки закупки масла"
        onCancel={() => setSettingsOpen(false)}
        onOk={saveSettings}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={settingsLoading}
        width={480}
        centered
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        {/* Секция 1: Очерёдность типов товаров */}
        <div style={{ marginBottom: 24 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Очерёдность типов товаров
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
            Товары сортируются сначала по типу в указанном порядке, затем по бренду, затем по наименованию. Остальные типы — по алфавиту.
          </Typography.Text>
          <div style={{ marginBottom: 8 }}>
            {typeOrder.map((typeName, idx) => (
              <div
                key={typeName}
                draggable
                onDragStart={() => setDragTypeIdx(idx)}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragTypeIdx === null || dragTypeIdx === idx) return;
                  const newOrder = [...typeOrder];
                  const [moved] = newOrder.splice(dragTypeIdx, 1);
                  newOrder.splice(idx, 0, moved);
                  setTypeOrder(newOrder);
                  setDragTypeIdx(idx);
                }}
                onDragEnd={() => setDragTypeIdx(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  marginBottom: 4,
                  background: dragTypeIdx === idx ? '#e6f4ff' : '#fafafa',
                  borderRadius: 4,
                  cursor: 'grab',
                  border: '1px solid #f0f0f0',
                }}
              >
                <MenuOutlined style={{ color: '#bfbfbf', marginRight: 8, fontSize: 12 }} />
                <span style={{ flex: 1 }}>{typeName}</span>
                <CloseOutlined
                  style={{ color: '#ff4d4f', fontSize: 12, cursor: 'pointer' }}
                  onClick={() => setTypeOrder(prev => prev.filter((_, i) => i !== idx))}
                />
              </div>
            ))}
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder="Добавить тип"
            value={newType}
            onChange={(val) => {
              if (val && !typeOrder.includes(val)) {
                setTypeOrder(prev => [...prev, val]);
              }
              setNewType(undefined);
            }}
            showSearch
            optionFilterProp="label"
            options={productTypes
              .filter(t => t.is_active && !typeOrder.includes(t.name))
              .map(t => ({ value: t.name, label: t.name }))}
          />
        </div>

        {/* Секция 2: Очерёдность брендов */}
        <div style={{ marginBottom: 24 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Очерёдность брендов
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
            Бренды в указанном порядке отображаются первыми внутри типа. Остальные — по алфавиту.
          </Typography.Text>
          <div style={{ marginBottom: 8 }}>
            {brandOrder.map((brand, idx) => (
              <div
                key={brand}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragIdx === null || dragIdx === idx) return;
                  const newOrder = [...brandOrder];
                  const [moved] = newOrder.splice(dragIdx, 1);
                  newOrder.splice(idx, 0, moved);
                  setBrandOrder(newOrder);
                  setDragIdx(idx);
                }}
                onDragEnd={() => setDragIdx(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  marginBottom: 4,
                  background: dragIdx === idx ? '#e6f4ff' : '#fafafa',
                  borderRadius: 4,
                  cursor: 'grab',
                  border: '1px solid #f0f0f0',
                }}
              >
                <MenuOutlined style={{ color: '#bfbfbf', marginRight: 8, fontSize: 12 }} />
                <span style={{ flex: 1 }}>{brand}</span>
                <CloseOutlined
                  style={{ color: '#ff4d4f', fontSize: 12, cursor: 'pointer' }}
                  onClick={() => setBrandOrder(prev => prev.filter((_, i) => i !== idx))}
                />
              </div>
            ))}
          </div>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Добавить бренд"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              onPressEnter={() => {
                const val = newBrand.trim();
                if (val && !brandOrder.includes(val)) {
                  setBrandOrder(prev => [...prev, val]);
                  setNewBrand('');
                }
              }}
            />
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                const val = newBrand.trim();
                if (val && !brandOrder.includes(val)) {
                  setBrandOrder(prev => [...prev, val]);
                  setNewBrand('');
                }
              }}
            />
          </Space.Compact>
        </div>

        {/* Секция 3: Очерёдность товаров */}
        <div style={{ marginBottom: 24 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Очерёдность товаров
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
            Товары в указанном порядке отображаются первыми внутри бренда. Остальные — по алфавиту.
          </Typography.Text>
          <div style={{ marginBottom: 8 }}>
            {modelOrder.map((model, idx) => (
              <div
                key={model}
                draggable
                onDragStart={() => setDragModelIdx(idx)}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragModelIdx === null || dragModelIdx === idx) return;
                  const newOrder = [...modelOrder];
                  const [moved] = newOrder.splice(dragModelIdx, 1);
                  newOrder.splice(idx, 0, moved);
                  setModelOrder(newOrder);
                  setDragModelIdx(idx);
                }}
                onDragEnd={() => setDragModelIdx(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  marginBottom: 4,
                  background: dragModelIdx === idx ? '#e6f4ff' : '#fafafa',
                  borderRadius: 4,
                  cursor: 'grab',
                  border: '1px solid #f0f0f0',
                }}
              >
                <MenuOutlined style={{ color: '#bfbfbf', marginRight: 8, fontSize: 12 }} />
                <span style={{ flex: 1 }}>{model}</span>
                <CloseOutlined
                  style={{ color: '#ff4d4f', fontSize: 12, cursor: 'pointer' }}
                  onClick={() => setModelOrder(prev => prev.filter((_, i) => i !== idx))}
                />
              </div>
            ))}
          </div>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Добавить товар"
              value={newModel}
              onChange={(e) => setNewModel(e.target.value)}
              onPressEnter={() => {
                const val = newModel.trim();
                if (val && !modelOrder.includes(val)) {
                  setModelOrder(prev => [...prev, val]);
                  setNewModel('');
                }
              }}
            />
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                const val = newModel.trim();
                if (val && !modelOrder.includes(val)) {
                  setModelOrder(prev => [...prev, val]);
                  setNewModel('');
                }
              }}
            />
          </Space.Compact>
        </div>

        {/* Секция 3: Пересылка писем от поставщиков */}
        <div style={{ marginBottom: 24 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Пересылка писем от поставщиков
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
            Входящие письма с прайсами и счетами пересылаются на этот адрес.
          </Typography.Text>
          <Input
            value={forwardEmail}
            onChange={(e) => setForwardEmail(e.target.value)}
            placeholder="oil@mobil1murmansk.ru"
          />
        </div>

        {/* Секция 4: Настройка доступа НЕВА */}
        <div>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Настройка доступа НЕВА
          </Typography.Text>
          <div style={{ marginBottom: 8 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Страница (web)</Typography.Text>
            <Input
              value={nevaConfig.url || ''}
              onChange={(e) => setNevaConfig(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://..."
              style={{ marginTop: 4 }}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Логин</Typography.Text>
            <Input
              value={nevaConfig.login || ''}
              onChange={(e) => setNevaConfig(prev => ({ ...prev, login: e.target.value }))}
              placeholder="Логин"
              style={{ marginTop: 4 }}
            />
          </div>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Пароль</Typography.Text>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={nevaConfig.password || ''}
              onChange={(e) => setNevaConfig(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Пароль"
              style={{ marginTop: 4 }}
              suffix={
                showPassword
                  ? <EyeOutlined onClick={() => setShowPassword(false)} style={{ cursor: 'pointer', color: '#8c8c8c' }} />
                  : <EyeInvisibleOutlined onClick={() => setShowPassword(true)} style={{ cursor: 'pointer', color: '#8c8c8c' }} />
              }
            />
          </div>
        </div>

        {/* Секция: Состояние прайсов */}
        <div style={{ marginTop: 8 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Состояние прайсов
          </Typography.Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography.Text>Прайс считается действующим:</Typography.Text>
            <InputNumber
              min={1}
              max={365}
              value={freshDays}
              onChange={v => setFreshDays(v ?? 2)}
              style={{ width: 70 }}
              size="small"
            />
            <Typography.Text type="secondary">дн.</Typography.Text>
          </div>
        </div>

        {/* Секция: Отступы таблицы */}
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Отступы таблицы
          </Typography.Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <Typography.Text style={{ width: 50, fontSize: 12 }}>Слева</Typography.Text>
            <Slider
              min={0} max={400} step={10}
              value={tablePadding.left}
              onChange={(v) => {
                const next = { ...tablePadding, left: v };
                setTablePadding(next);
                localStorage.setItem('oil-table-padding', JSON.stringify(next));
              }}
              style={{ flex: 1 }}
            />
            <Typography.Text style={{ width: 40, textAlign: 'right', fontSize: 12 }}>{tablePadding.left}px</Typography.Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Typography.Text style={{ width: 50, fontSize: 12 }}>Справа</Typography.Text>
            <Slider
              min={0} max={400} step={10}
              value={tablePadding.right}
              onChange={(v) => {
                const next = { ...tablePadding, right: v };
                setTablePadding(next);
                localStorage.setItem('oil-table-padding', JSON.stringify(next));
              }}
              style={{ flex: 1 }}
            />
            <Typography.Text style={{ width: 40, textAlign: 'right', fontSize: 12 }}>{tablePadding.right}px</Typography.Text>
          </div>
        </div>
      </Modal>

    </div>
  );
}
